import m from 'mithril';
import Requester from '../../shared/request';
import Gateway from '../../shared/app_settings';
import Toasts from '../../shared/toasts';
import i18n from '../../shared/i18n/i18n';
import SessionController from '../../shared/session';
import Loading from '../../shared/loading';
import RouterService from '../../shared/services/router.service';
import ModalManager from "../../shared/modal";
import Modal_Scorm_Adblocker from "./modal_scorm_adblocker";
import ScormUtilities from '../../shared/utilities/scorm_utilities';

let originalUnit, interactiveUnit, enrId, exitCallback, loading, courseId, isReview;
let modernPlayerReady = false;

function useModernPlayer() {
    return ScormUtilities.useModernPlayer();
}

function loadPlayer() {
    loading = true;
    Requester.get(Gateway.public + "units/interactive/" + originalUnit.id + "/launch?enrolmentId=" + enrId + "&unitEnrolmentId=" + originalUnit.enrolment.id)
        .then(function (result) {
            interactiveUnit = null;
            interactiveUnit = result;
        })
        .catch(function (error) {
            Toasts.add("error", i18n.t("error"), i18n.t(error.response.properties.errorCode));
        })
        .finally(function () {
            loading = false;
            if (!useModernPlayer()) {
                setTimeout(getAccessedOn, 5000);
            }
        });
}

function activateModern() {
    if (!modernPlayerReady) {
        return; // Will be called again when 'ready' message is received
    }

    const storageOrigin = SessionController.session.environment.storageUrl;
    document.getElementById("player-top").contentWindow.postMessage({
        method: "configure",
        config: {
            unitEnrolmentId: interactiveUnit.enrolment.id,
            unitVersion: interactiveUnit.version,
            scormVersion: interactiveUnit.scormVersion,
            parentOrigin: window.location.origin
        }
    }, storageOrigin);

    document.getElementById("player-top").contentWindow.postMessage({
        method: "activate",
        data: {
            scormSource: interactiveUnit.scormSource,
            cmiData: interactiveUnit.enrolment.cmiData
        }
    }, storageOrigin);
}

function activateLegacy() {
    let enr = {
        enrolmentId: enrId,
        unitEnrolmentId: interactiveUnit.enrolment.id,
        unitVersion: interactiveUnit.version,
        source: interactiveUnit.scormSource,
        data: interactiveUnit.enrolment.cmiData,
        version: interactiveUnit.scormVersion
    };

    document.getElementById("player-top").contentWindow.postMessage({
        method: "activate",
        data: enr
    }, SessionController.session.environment.storageUrl);
}

function activate() {
    if (useModernPlayer()) {
        // Modern player: activation is deferred until we receive the 'ready' message.
        // If ready already arrived before onload (unlikely but safe), trigger now.
        if (modernPlayerReady) {
            activateModern();
        }
    } else {
        activateLegacy();
    }
}

function deactivate() {
    const playerIframe = document.getElementById("player-top");
    if (playerIframe && playerIframe.contentWindow) {
        playerIframe.contentWindow.postMessage({method: "deactivate"}, SessionController.session.environment.storageUrl);
    }

    if (courseId) {
        RouterService.navigateToPublicCourse(courseId);
    }
}

function save(data, finish) {
    let content = {
        unitEnrolmentId: interactiveUnit.enrolment.id,
        unitVersion: interactiveUnit.version,
        scormVersion: interactiveUnit.scormVersion,
        data: data
    };

    Requester.post(Gateway.public + "units/interactive/save-progress", content)
        .then(function (result) {
            if (finish) {
                exitCallback();
            }

            if (result.isCompleted) {
                Toasts.add("success", i18n.t("unit_completed"));
                m.redraw();
            }
        })
        .catch(function (error) {
            console.error('SCORM save failed:', error);
            // Defensive error handling for various error formats
            const errorMessage = error?.response?.properties?.errorCode
                ? i18n.t(error.response.properties.errorCode)
                : error?.message || 'Failed to save progress';
            Toasts.add("error", i18n.t("error"), errorMessage);
        });
}

function receiveMessage(event) {
    const storageUrl = SessionController.session.environment.storageUrl;

    // Strip trailing slash for consistent comparison (origin never has one)
    const expectedOrigin = storageUrl.replace(/\/+$/, '');

    if (event.origin !== expectedOrigin)
        return;

    if (event.data.method === "ready" && useModernPlayer()) {
        modernPlayerReady = true;
        activateModern();
        return;
    }

    if (event.data.method === "scorm-save") {
        // Modern player sends structured data
        save(event.data.data.cmiData, false);
    }

    if (event.data.method === "scorm-finish") {
        // Modern player sends final save
        save(event.data.data.cmiData, true);
    }

    // Legacy player messages (same as before)
    if (event.data.method === "update")
        save(event.data.data);

    if (event.data.method === "finish") {
        save(event.data.data, true);
    }
}
function getAccessedOn() {
    Requester.get(Gateway.public + "units/interactive/" + interactiveUnit.enrolment.id + "/accessed-on")
        .then(function (result) {
            if (!result.value) {
                adBlockerPrompt();
            }
        });
}

function adBlockerPrompt() {
    ModalManager.open(i18n.t("action_required"), [], Modal_Scorm_Adblocker, {closeScormCourse: deactivate}, deactivate);
}

function getHeaderName() {
    if (!interactiveUnit) {
        return null;
    }

    return SessionController.session.identity.name + ": " + interactiveUnit.name;
}

const Component_Player = {
    oninit: function (vnode) {
        enrId = vnode.attrs.enrolmentId;
        originalUnit = vnode.attrs.unit;
        isReview = !!vnode.attrs.isReview;
        loadPlayer();
        exitCallback = vnode.attrs.callback;
        courseId = vnode.attrs.courseId;
        window.addEventListener("message", receiveMessage, false);

    },
    onremove: function () {
        window.removeEventListener("message", receiveMessage);
        modernPlayerReady = false;
    },
    view: function () {
        const playerFile = useModernPlayer()
            ? SessionController.session.environment.storageUrl + "/scorm-player/scorm_player_v2.html"
            : SessionController.session.environment.storageUrl + "/scorm-player/scorm_player.html";

        return [
            m("#player", [
                m(".flex-row justify-between align-center padding", [
                    m(".flex-row align-center", [
                        m("h3.ellipsis.mb-0", getHeaderName()),
                        isReview ? m("span.review-badge.ml-2", i18n.t("review_mode")) : null
                    ]),
                    m("button.btn-cta", {onclick: deactivate }, isReview ? i18n.t("close") : i18n.t("SaveAndExit"))
                ]),
                !loading ? [
                    interactiveUnit ? [
                        m("iframe#player-top", {
                            src: playerFile,  // Player served from storage domain (same-origin as content)
                            onload: activate,
                            allow: "fullscreen"
                        })
                    ] : i18n.t("cant_load")
                ] : m(Loading)
            ])
        ];
    }
};
export default Component_Player;
